import { useEffect, useState } from 'preact/hooks'
import { memo } from 'preact/compat'
import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-preact'

interface TimerProps {
  expirationTime: string
  onComplete?: () => void
}

export function Timer({ expirationTime, onComplete }: TimerProps) {
  const { t } = useTranslation()
  const [timeLeft, setTimeLeft] = useState(0)
  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    const calculateRemainingTime = () => {
      const now = new Date()
      const expiration = new Date(expirationTime)
      const remainingMs = expiration.getTime() - now.getTime()
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000))

      setTimeLeft(remainingSeconds)

      // If time expired, trigger the handler
      if (remainingSeconds <= 0) {
        onComplete?.()
      }
    }

    calculateRemainingTime()

    const interval = setInterval(calculateRemainingTime, 1000)

    return () => clearInterval(interval)
  }, [expirationTime, onComplete])

  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60

  const isMoreThanFiveMinutes = timeLeft > 5 * 60

  const containerClass = `inline-flex items-center rounded-xl gap-1 px-3 sm:px-2 py-1 sm:py-1.5 rounded-button relative cursor-default text-sm ${
    isMoreThanFiveMinutes
      ? 'bg-green-50 border border-green-200 text-green-900'
      : 'bg-yellow-50 border border-yellow-200 text-yellow-900'
  }`

  const iconClass = `w-4 h-4 mr-1 ${isMoreThanFiveMinutes ? 'text-green-900' : 'text-yellow-900'}`

  return (
    <div
      className={containerClass}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-label={t('components.timer.remainingSessionTime')}
      tabIndex={0}
    >
      <Clock className={iconClass} aria-hidden='true' />
      <span className='font-medium tabular-nums leading-none'>
        {minutes.toString().padStart(2, '0')}:
        {seconds.toString().padStart(2, '0')}
      </span>
      {showTooltip && (
        <div className='absolute top-full right-0 mt-2 px-2.5 py-1.5 bg-gray-800 text-white text-xs rounded-lg shadow-lg whitespace-nowrap z-30'>
          {t('components.timer.remainingSessionTime')}
          <div className='absolute -top-2 right-2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800'></div>
        </div>
      )}
    </div>
  )
}

export const MemoizedTimer = memo(Timer)
